import React, { useRef } from "react";
import { Tooltip } from "@mui/material";
import CloseIcon from "../../../images/iconclose.svg";
import DeleteKeyFrame from "../../../images/DeleteKeyFrame.svg";
import DeleteKeyFrameDisable from "../../../images/DeleteKeyFrameDisable.svg";
import Paste from "../../../images/Paste.svg";
import pasteBlue from "../../../images/BluePaste.svg";
import CopyKeyFrame from "../../../images/CopyKeyFrame.svg";
import CopyKeyFrameDisable from "../../../images/CopyKeyFrameDisable.svg";
import { OutsideClickHandler } from "../../../Uitls/Util";

// Clicks on these controls open/retarget the popup — don't treat them as "outside".
const KEYFRAME_POPUP_OPENER_SELECTOR = [
  ".hitAreaTimelineKeyframeMarker",
  ".hitAreaTimelineAddKeyframeButton",
  ".hitAreaTimelineAddKeyframeButtonWrap",
  ".hitAreaTimelineEventMarker",
].join(", ");

export default function HitAreaKeyframePopup({
  keyframePopupState,
  hasKeyframeClipboard,
  copySelectedKeyframeDimensions,
  pasteSelectedKeyframeDimensions,
  isNewKeyframe,
  deleteSelectedKeyframe,
  closeKeyframePopup,
  keyframeEditorErrors,
  keyframeEditorWidth,
  keyframeEditorHeight,
  onKeyframeEditorWidthChange,
  onKeyframeEditorHeightChange,
  minRectSize,
  isSelectedHitAreaKeyframeEditable,
  isKeyframeEditorInvalid,
  updateSelectedKeyframeDimensions,
}) {
  const popupRef = useRef(null);

  OutsideClickHandler(
    popupRef,
    (event) => {
      if (event?.target?.closest?.(KEYFRAME_POPUP_OPENER_SELECTOR)) {
        return;
      }
      closeKeyframePopup();
    },
    keyframePopupState?.isOpen
  );

  const blockInvalidDimensionInputKeys = (event) => {
    if (["-", "+", "e", "E"].includes(event.key)) {
      event.preventDefault();
    }
  };

  const sanitizeDimensionText = (value) =>
    String(value ?? "")
      .replace(/-/g, "")
      .replace(/[^\d.]/g, "");

  const handleDimensionPaste = (event, currentValue, onChange) => {
    event.preventDefault();
    const pasted = sanitizeDimensionText(
      event.clipboardData?.getData("text") || ""
    );
    if (!pasted) return;

    const input = event.target;
    const value = String(currentValue ?? "");
    const start =
      typeof input.selectionStart === "number" ? input.selectionStart : value.length;
    const end =
      typeof input.selectionEnd === "number" ? input.selectionEnd : value.length;
    onChange(`${value.slice(0, start)}${pasted}${value.slice(end)}`);
  };

  return (
    <div
      ref={popupRef}
      className={`hitAreaKeyframePopup ${keyframePopupState.placement === "below" ? "isBelow" : "isAbove"}`}
      style={{
        left: keyframePopupState.left,
        top: keyframePopupState.top,
      }}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div
        className="hitAreaKeyframePopupArrow"
        style={{ left: keyframePopupState.arrowLeft }}
      />
      <div className="hitAreaKeyframePopupHeader">
        <div className="hitAreaKeyframePopupTitle">Hit Area Animation</div>
        <div className="hitAreaKeyframePopupActions copyDelete">
          <Tooltip title="Paste" placement="top" arrow>
            <img
              src={hasKeyframeClipboard ? pasteBlue : Paste}
              onClick={
                hasKeyframeClipboard ? () => pasteSelectedKeyframeDimensions() : undefined
              }
              alt="Paste"
            />
          </Tooltip>
          <Tooltip title="Copy" placement="top" arrow>
            {isNewKeyframe ? (
              <img src={CopyKeyFrameDisable} alt="Copy" />
            ) : (
              <img
                onClick={() => {
                  copySelectedKeyframeDimensions();
                }}
                src={CopyKeyFrame}
                alt="Copy"
              />
            )}
          </Tooltip>
          <Tooltip title="Delete" placement="top" arrow>
            {isNewKeyframe ? (
              <img src={DeleteKeyFrameDisable} alt="Delete" />
            ) : (
              <img
                onClick={() => {
                  deleteSelectedKeyframe();
                }}
                src={DeleteKeyFrame}
                alt="Delete"
              />
            )}
          </Tooltip>
          <Tooltip title="Close" placement="top" arrow>
            <img
              src={CloseIcon}
              onClick={closeKeyframePopup}
              alt="Close"
              className="img-fluid closeIconn"
            />
          </Tooltip>
        </div>
      </div>
      <div className="hitAreaKeyframePopupDivider" />
      <div className="hitAreaKeyframePopupFields">
        <div className="hitAreaKeyframePopupFieldGroup">
          <label
            className={`hitAreaKeyframePopupField ${keyframeEditorErrors.width ? "hasError" : ""}`}
          >
            <span>W</span>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={keyframeEditorWidth}
              disabled={!isSelectedHitAreaKeyframeEditable}
              onKeyDown={blockInvalidDimensionInputKeys}
              onPaste={(event) =>
                handleDimensionPaste(
                  event,
                  keyframeEditorWidth,
                  onKeyframeEditorWidthChange
                )
              }
              onChange={(event) => {
                onKeyframeEditorWidthChange(
                  sanitizeDimensionText(event.target.value)
                );
              }}
            />
          </label>
          {keyframeEditorErrors.width && (
            <span className="hitAreaKeyframePopupFieldError">
              {keyframeEditorErrors.width}
            </span>
          )}
        </div>
        <div className="hitAreaKeyframePopupFieldGroup">
          <label
            className={`hitAreaKeyframePopupField ${keyframeEditorErrors.height ? "hasError" : ""}`}
          >
            <span>H</span>
            <input
              type="text"
              inputMode="decimal"
              autoComplete="off"
              value={keyframeEditorHeight}
              disabled={!isSelectedHitAreaKeyframeEditable}
              onKeyDown={blockInvalidDimensionInputKeys}
              onPaste={(event) =>
                handleDimensionPaste(
                  event,
                  keyframeEditorHeight,
                  onKeyframeEditorHeightChange
                )
              }
              onChange={(event) => {
                onKeyframeEditorHeightChange(
                  sanitizeDimensionText(event.target.value)
                );
              }}
            />
          </label>
          {keyframeEditorErrors.height && (
            <span className="hitAreaKeyframePopupFieldError">
              {keyframeEditorErrors.height}
            </span>
          )}
        </div>
      </div>
      <button
        type="button"
        className={`hitAreaKeyframePopupUpdateButton ${isKeyframeEditorInvalid || !isSelectedHitAreaKeyframeEditable ? "isDisabled" : ""}`}
        title={
          !isSelectedHitAreaKeyframeEditable
            ? "Unlock selected hit area to update keyframe"
            : isKeyframeEditorInvalid
              ? "Enter valid W and H (minimum 5px)"
              : isNewKeyframe
                ? "Add keyframe with these dimensions"
                : "Update keyframe dimensions"
        }
        onClick={updateSelectedKeyframeDimensions}
        disabled={isKeyframeEditorInvalid || !isSelectedHitAreaKeyframeEditable}
      >
        {isNewKeyframe ? "APPLY" : "UPDATE"}
      </button>
    </div>
  );
}
